/**
 * Runtime 主題設定（依 domain 動態決定，單一 docker image 服務多品牌）。
 *
 * 流程：App 啟動時依當前 domain 決定用什麼 ThemeConfig
 * （顏色、logo / asset URL…），套用顏色到 CSS 變數，並把 config 存成單例供元件讀取。
 *
 * 注意：
 * - 顏色透過 CSS 變數（colors.scss 內 $x → var(--x)），未設定時用 fallback 預設色。
 *   config.colors 的 key 為 CSS 變數名（不含 --），會被 setProperty 成 --<key>。
 * - logo 等 asset 以「URL 字串」提供，不走 webpack import，才能無限擴充。
 *   元件在沒有 config 提供 URL 時，仍 fallback 回原本 bundled 的 asset。
 * - i18n 以 addResourceBundle 深層覆蓋相同 key，未覆蓋的 key 沿用 i18n.ts 內 build 的預設翻譯。
 * - otherSettings 以 Object.assign 覆蓋 src/config.ts（config）同名 key；因在 loading gate
 *   打開前就套用，render 時讀 config 即為覆蓋後的值。
 *
 * ── 新增一個品牌（theme）的步驟 ───────────────────────────────────────────
 * 假設新品牌 key = 'foo'、domain 含 'foo'：
 * 1. SUPPORTED_THEMES 加一筆：{ key: 'foo', matchesHost: (h) => h.includes('foo') }
 * 2. 放靜態檔到 public/themes/foo/：
 *    - 圖片：<assetKey>.<ext>，檔名須等於 asset key（如 pmax-logo.svg）
 *    - 翻譯：locales/<file>.json（file 用 en / ja-jpn / zh-tw，見 LOCALE_FILE_TO_LNG）
 * 3. 寫 public/themes/foo/theme-config.json（所有欄位皆可選，只填要覆蓋的）：
 *    {
 *      "colors":   { "header": "#000000" },          // CSS 變數覆蓋
 *      "assets":   { "pmax-logo": "svg" },            // key → 副檔名（只列要換的）
 *      "locales":  ["en", "ja-jpn", "zh-tw"],         // 要覆蓋的翻譯檔名（只列有翻的）
 *      "otherSettings": { "reportMetric1Color": "#000000" } // config 覆蓋
 *    }
 * 完全不用改元件、不用重 build 邏輯。
 * 本機預覽：設 REACT_APP_THEME=<key> 再 craco start（可參考 package.json 的 start-rocket）。
 * ─────────────────────────────────────────────────────────────────────────
 */

import i18n from 'i18n';
import appConfig from 'config';

/**
 * theme json（public/themes/<theme>/theme-config.json）的原始格式。
 * assets / locales 只寫精簡資訊，URL 由 fetchThemeConfig 依固定 pattern 組出。
 */
type RawThemeConfig = {
  /** 要 override 的顏色：CSS 變數名（不含 --）→ 色值，例如 { header: '#000000' } */
  readonly colors?: Readonly<Record<string, string>>;
  /** asset：key（asset 名稱，須與檔名一致）→ 副檔名（svg / png…） */
  readonly assets?: Readonly<Record<string, string>>;
  /** 要 override 的 i18n locale 檔名陣列（如 ['en', 'ja-jpn', 'zh-tw']） */
  readonly locales?: ReadonlyArray<string>;
  /** 其他 app config 覆蓋：key → 值（如 reportMetric1Color） */
  readonly otherSettings?: Readonly<Record<string, unknown>>;
};

/** 組好 URL、可直接套用的 runtime 主題設定。 */
export type ThemeConfig = {
  /** 要 override 的顏色：CSS 變數名（不含 --）→ 色值 */
  readonly colors?: Readonly<Record<string, string>>;
  /** asset：key → 完整 URL（供 getAsset / resolveAsset 取用） */
  readonly assets?: Readonly<Record<string, string>>;
  /** i18n 覆蓋：i18next 語言 code → 該語言翻譯 JSON 的 URL */
  readonly locales?: Readonly<Record<string, string>>;
  /** 其他 app config 覆蓋：key → 值，會 Object.assign 到 src/config.ts 上 */
  readonly otherSettings?: Readonly<Record<string, unknown>>;
};

/** 已載入並套用的 config（單例）。元件可透過 getThemeConfig() 讀取。 */
let currentThemeConfig: ThemeConfig = {};

/**
 * 支援的 themes 清單：每個 theme 用 matchesHost 判斷是否適用於當前 domain。
 * 新增品牌只要在這裡加一筆，並放上對應的 public/themes/<key>/theme-config.json（+ assets / locales）。
 */
type ThemeDefinition = {
  readonly key: string;
  readonly matchesHost: (host: string) => boolean;
};

const SUPPORTED_THEMES: ReadonlyArray<ThemeDefinition> = [
  { key: 'rocket', matchesHost: (host) => host.includes('rckt') }
];

/**
 * locale 檔名 → i18next 語言 code。
 * theme json 的 locales 列的是「檔名」（en / ja-jpn / zh-tw），但 addResourceBundle 需要語言
 * code（en / ja / zh-TW），故用此對照轉換。
 */
const LOCALE_FILE_TO_LNG: Readonly<Record<string, string>> = {
  en: 'en',
  'ja-jpn': 'ja',
  'zh-tw': 'zh-TW'
};

/**
 * 依 theme key 與要 override 的 locale 檔名陣列，組出 { i18next code → URL }。
 * 檔案固定放在 /themes/<theme>/locales/<file>.json；未知檔名會被略過。
 */
function buildLocaleUrls(
  themeKey: string,
  files?: ReadonlyArray<string>
): Record<string, string> {
  const locales: Record<string, string> = {};
  for (const file of files ?? []) {
    const lng = LOCALE_FILE_TO_LNG[file];
    if (lng) {
      locales[lng] = `/themes/${themeKey}/locales/${file}.json`;
    }
  }
  return locales;
}

/**
 * 依 theme key 與 assets 設定（key → 副檔名）組出各 asset 的固定 URL：
 * /themes/<theme>/<key>.<ext>。只有列在 json 的 asset 會被覆蓋，其餘 fallback bundled。
 */
function buildAssetUrls(
  themeKey: string,
  assets?: Readonly<Record<string, string>>
): Record<string, string> {
  const result: Record<string, string> = {};
  if (assets) {
    for (const [name, ext] of Object.entries(assets)) {
      result[name] = `/themes/${themeKey}/${name}.${ext}`;
    }
  }
  return result;
}

/**
 * 解析當前要套用的 theme key。
 * local 測試：REACT_APP_THEME（npm run start-rocket 設定）優先；
 * 否則依 domain 掃描 SUPPORTED_THEMES。找不到回傳 undefined（用預設、不覆蓋）。
 */
function resolveThemeKey(host: string): string | undefined {
  const forced = import.meta.env.REACT_APP_THEME;
  if (forced) {
    return SUPPORTED_THEMES.some((theme) => theme.key === forced)
      ? forced
      : undefined;
  }
  return SUPPORTED_THEMES.find((theme) => theme.matchesHost(host))?.key;
}

/**
 * 依當前 domain（或 REACT_APP_THEME）取得 ThemeConfig。
 * 找到對應 theme 就 fetch public/themes/<key>/theme-config.json；找不到或失敗則回傳空（用預設）。
 */
export async function fetchThemeConfig(): Promise<ThemeConfig> {
  const themeKey = resolveThemeKey(window.location.hostname);
  if (!themeKey) {
    return {};
  }
  try {
    const res = await fetch(`/themes/${themeKey}/theme-config.json`);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const raw: RawThemeConfig = await res.json();
    // assets（json 內為副檔名）與 locales（json 內為檔名陣列）都依 theme key 組固定 pattern URL
    return {
      colors: raw.colors,
      otherSettings: raw.otherSettings,
      assets: buildAssetUrls(themeKey, raw.assets),
      locales: buildLocaleUrls(themeKey, raw.locales)
    };
  } catch (e) {
    console.error(`failed to load theme config for "${themeKey}"`, e);
    return {};
  }
}

/** 套用 ThemeConfig：設定 CSS 變數、覆蓋 app config，並存成單例。 */
export function applyThemeConfig(config: ThemeConfig): void {
  currentThemeConfig = config;
  if (config.colors) {
    Object.entries(config.colors).forEach(([name, value]) => {
      document.documentElement.style.setProperty(`--${name}`, value);
    });
  }
  if (config.otherSettings) {
    // 覆蓋 src/config.ts（config）同名 key；元件 render 時讀到的即為覆蓋後的值
    Object.assign(appConfig, config.otherSettings);
  }
}

/** 取得目前已套用的 ThemeConfig（供元件決定 logo 等 asset）。 */
export function getThemeConfig(): ThemeConfig {
  return currentThemeConfig;
}

/**
 * 取得 config.assets 裡（依 domain 提供）的 asset URL；未提供則回傳 undefined。
 * 適合呼叫端需要「有 override 才用、且要清掉指向 bundled 的 srcSet」的情境。
 */
export function getAsset(name: string): string | undefined {
  return currentThemeConfig.assets?.[name];
}

/**
 * 解析 asset：優先用 config.assets（依 domain 提供）的 URL，
 * 沒有則 fallback 回傳傳入的 bundled asset（webpack import 進來的預設圖）。
 * @param name     asset key，對應 config.assets，例如 'tenmax-logo'
 * @param fallback 預設 bundled asset（import 進來的字串）
 */
export function resolveAsset(name: string, fallback: string): string {
  return getAsset(name) ?? fallback;
}

/**
 * 移除空值（''/null/undefined）與空物件。
 * rocket locale 為「原始翻譯」（未在 build 時 merge），未翻譯的欄位可能是空字串；
 * 若直接覆蓋會把 default 的字串清空，故先剔除空值，等同「rocket 空欄 fallback default」
 */
function pruneEmpty(value: unknown): unknown {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const pruned = pruneEmpty(v);
    if (pruned !== undefined) {
      out[k] = pruned;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * 套用 i18n 覆蓋：fetch 各語言 JSON，剔除空值後用 addResourceBundle 深層合併覆蓋相同 key。
 * 單一語言失敗只記錄、不中斷其他語言；未覆蓋（或空欄）的 key 沿用 build 的預設翻譯。
 */
export async function applyLocales(
  locales?: Readonly<Record<string, string>>
): Promise<void> {
  if (!locales) {
    return;
  }
  await Promise.all(
    Object.entries(locales).map(async ([lng, url]) => {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const overrides = pruneEmpty(json) ?? {};
        // deep = true（巢狀合併）、overwrite = true（覆蓋既有 key）
        i18n.addResourceBundle(lng, 'translation', overrides, true, true);
      } catch (e) {
        console.error(`failed to load locale ${lng} from ${url}`, e);
      }
    })
  );
}

/**
 * 取得並套用 ThemeConfig（顏色 / asset 單例 / i18n 覆蓋）。
 * 失敗時不丟出例外，保留 colors.scss 的 fallback、bundled asset 與預設翻譯，避免擋住整個 App。
 */
export async function loadAndApplyThemeConfig(): Promise<void> {
  try {
    const config = await fetchThemeConfig();
    applyThemeConfig(config);
    await applyLocales(config.locales);
  } catch (e) {
    console.error('failed to load theme config', e);
  }
}
